import type { FunctionComponent } from "preact";
import { useTranslator } from "../../hooks/i18n.ts";
import { useState, useEffect, useRef } from "preact/hooks";
import { FooterRecord } from "../../components/footer_record/footer_record.tsx";
import { HeaderNav } from "../../components/header_nav/header_nav.tsx";
import { FFmpegLoader } from "../../components/ffmpeg_loader/ffmpeg_loader.tsx";
import { FarpovCameraSelect } from "../../components/farpov_camera_select/farpov_camera_select.tsx";
import { clearVideoPhotoCache, DownloadVideoPhotosTask, getAllPhotos } from "../../store/task/photos.ts";
import { Icon } from "../../components/icon/icon.tsx";
import { useToast } from "../../components/toast/toast.tsx";
import { waitTaskResult } from "../../store/task/task.ts";
import { EncodeVideoTask } from "../../store/task/encode_video_task.ts";
import AdvanceUserRequiredBanner from "../../components/vip_required_banner/advance_user_required_banner.tsx";
import { CacheKV } from "../../utils/cache_kv.ts";
import { useStore } from "@nanostores/preact";
import { $isLogined } from "../../store/user_info.ts";
import { refreshCameraPage } from "../../store/cameras.ts";
import { usePageRefresh } from "../../hooks/use_page_refresh.ts";

const TIME_DEFAULT = Date.now();
const VIDEO_PREVIEW_CACHE = "video_preview_cache";
const LAST_VIDEO_KEY = "last_video.webm";

const unixMsToLocalISOString = (time: number) => {
    const localTime = new Date(time);
    const offsetMs = localTime.getTimezoneOffset() * 60 * 1000;
    const fakeTime = new Date(time - offsetMs);
    const localISOString = fakeTime.toISOString().substring(0, 16);
    return localISOString;
};

const setDatetimeInputValue = (setValue: (v: number) => unknown, evt: Event) => {
    const elem = evt.target as HTMLInputElement;
    const timestamp = Date.parse(elem.value); // time zone offset applied by the Date class
    if (!Number.isNaN(timestamp)) {
        setValue(timestamp);
    }
};

const getUnixSecondFromLocalMs = (localTimeMs: number) => {
    return Math.floor(localTimeMs / 1000);
};

export const MakeVideoPage: FunctionComponent = (_) => {
    const [selectCamera, setSelectCamera] = useState(-1);
    const [startTime, setStartTime] = useState(TIME_DEFAULT - (3 * 24 * 60 * 60 * 1000));
    const [endTime, setEndTime] = useState(TIME_DEFAULT);
    const [quality, setQuality] = useState(23); // Default improved from 31 to 23 for better quality
    const [speed, setSpeed] = useState(0);
    const [filterEnabled, setFilterEnabled] = useState(false);
    const [filterStartTime, setFilterStartTime] = useState("08:00");
    const [filterEndTime, setFilterEndTime] = useState("18:00");
    const [processing, setProcessing] = useState(false);
    const [audioFile, setAudioFile] = useState<File | null>(null);
    const [prog, setProg] = useState([0, 100]);
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const t = useTranslator();
    const audioInputRef = useRef<HTMLInputElement>(null);
    const startTimeText = unixMsToLocalISOString(startTime);
    const endTimeText = unixMsToLocalISOString(endTime);
    const toast = useToast();
    const isLogined = useStore($isLogined);
    const refresh = usePageRefresh({
        pageKey: "make-video", enabled: isLogined, canAutoRefresh: !processing,
        refresh: refreshCameraPage,
    });

    useEffect(() => {
        if (globalThis.history?.state?.cameraId) {
            const cameraId = Number.parseInt(globalThis.history?.state?.cameraId);
            if (!Number.isNaN(cameraId)) {
                setSelectCamera(cameraId);
            }
        }
    }, []);

    useEffect(() => {
        document.title = t({
            zh: "照片生成视频",
            en: "Encoding Video",
        });

        // Try to load cached video
        const loadCachedVideo = async () => {
            try {
                const kv = await CacheKV.open(VIDEO_PREVIEW_CACHE);
                const buf = await kv.get(LAST_VIDEO_KEY);
                if (buf) {
                    const blob = new Blob([buf], { type: "video/webm" });
                    const url = URL.createObjectURL(blob);
                    setVideoUrl(url);
                }
            } catch (e) {
                console.error("Failed to load cached video", e);
            }
        };
        loadCachedVideo();

        return () => {
            if (videoUrl) {
                URL.revokeObjectURL(videoUrl);
            }
        };
    }, [t]);

    const onDownloadAndFFmpegClick = async () => {
        try {
            setProcessing(true);
            // query all photos
            const photos = await getAllPhotos(
                selectCamera,
                getUnixSecondFromLocalMs(endTime),
                getUnixSecondFromLocalMs(startTime),
                500,
            );
            toast(
                t({ zh: `一共${photos.length}张照片`, en: `Total ${photos.length} photos` }),
                "normal",
                "long",
            );
            if (photos.length <= 0) {
                toast(
                    t({ zh: `没有照片`, en: `No photos` }),
                    "error",
                    "long",
                );
                return;
            }

            let photosToProcess = photos;
            if (filterEnabled) {
                const [startH, startM] = filterStartTime.split(":").map(Number);
                const [endH, endM] = filterEndTime.split(":").map(Number);
                const startMinutes = startH * 60 + startM;
                const endMinutes = endH * 60 + endM;

                photosToProcess = photos.filter(p => {
                    const date = new Date(p.photo_time * 1000);
                    const currentMinutes = date.getHours() * 60 + date.getMinutes();
                    // Handle range crossing midnight if needed, but for simple day range:
                    if (startMinutes <= endMinutes) {
                        return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
                    } else {
                        // Range crosses midnight (e.g. 22:00 to 06:00)
                        return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
                    }
                });

                toast(
                    t({ 
                        zh: `过滤后剩余 ${photosToProcess.length} 张照片 (原 ${photos.length} 张)`, 
                        en: `Filtered: ${photosToProcess.length} photos (Original: ${photos.length})` 
                    }),
                    "normal",
                    "long",
                );

                if (photosToProcess.length <= 0) {
                    toast(
                        t({ zh: `过滤后没有照片`, en: `No photos after filter` }),
                        "error",
                        "long",
                    );
                    return;
                }
            }

            // sort from earlier to later
            photosToProcess.sort((a, b) => a.photo_time - b.photo_time);
            const dlTask = new DownloadVideoPhotosTask(photosToProcess);
            dlTask.start();
            await waitTaskResult(dlTask, (current, total) => {
                setProg([current, total]);
                toast(
                    t({ zh: `下载照片: ${current}/${total}`, en: `Download photos: ${current}/${total}` }),
                    "normal",
                    "short",
                );
            });
            toast(
                t({ zh: `编码视频...`, en: `Encoding video...` }),
                "normal",
                "short",
            );
            const files = dlTask.getDownloadedFiles();
            const evTask = new EncodeVideoTask(files, quality, speed, 25, 1080, audioFile);
            evTask.start();
            await waitTaskResult(evTask, (current, total) => {
                setProg([current, total]);
                toast(
                    t({ zh: `编码视频: ${current}/${total}`, en: `Encoding video: ${current}/${total}` }),
                    "normal",
                    "short",
                );
            });
            
            // Generate Blob URL instead of auto download
            const blob = evTask.getOutputBlob();
            if (videoUrl) URL.revokeObjectURL(videoUrl);
            const url = URL.createObjectURL(blob);
            setVideoUrl(url);

            // Cache the video
            try {
                const kv = await CacheKV.open(VIDEO_PREVIEW_CACHE);
                await kv.set(LAST_VIDEO_KEY, blob);
            } catch (e) {
                console.error("Failed to cache video", e);
            }
            
            toast(
                t({ zh: `视频生成完成`, en: `Video generation completed` }),
                "success",
                "short",
            );
        } catch (err) {
            console.error(err);
            toast((err as Error).toString(), "error", "long");
        } finally {
            setProcessing(false);
        }
    };

    const setStartTimeBasedOnEndTime = (offsetDays: number) => {
        setStartTime(endTime + (offsetDays * 24 * 60 * 60 * 1000));
    };

    const clearCache = async () => {
        if (!confirm(t({zh: "确定要清空已下载的照片缓存吗？下次生成视频需要重新下载。", en: "Are you sure to clear photo cache? You will need to re-download photos next time."}))) {
            return;
        }
        try {
            await clearVideoPhotoCache();
            toast(t({zh: "缓存已清空", en: "Cache cleared"}), "success");
        } catch (e) {
            console.error(e);
            toast(t({zh: "清空失败", en: "Failed to clear cache"}), "error");
        }
    };

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    return (
        <>
            <div class="farpov_top_container">
                {refresh.error && <p role="alert">{refresh.error}</p>}
                <FFmpegLoader>
                    <div class="farpov_step_container">
                        
                        {/* Step 1: Source Selection */}
                        <article class="farpov_card">
                            <header>
                                <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                                    <strong>1. {t({ zh: "选择素材", en: "Select Source" })}</strong>
                                    <button class="outline secondary" style={{padding: "4px 8px", fontSize: "0.8em", width: "auto"}} onClick={clearCache}>
                                        {t({zh: "清空缓存", en: "Clear Cache"})}
                                    </button>
                                </div>
                            </header>
                            <fieldset class="grid">
                                <label>
                                    {t({ zh: "选择相机", en: "Select Camera" })}
                                    <FarpovCameraSelect
                                        disabled={processing}
                                        selectCameraId={selectCamera}
                                        setSelectCameraId={setSelectCamera}
                                    />
                                </label>
                            </fieldset>
                            <fieldset class="grid">
                                <label>
                                    {t({ zh: "开始日期", en: "Start Date" })}
                                    <input
                                        type="datetime-local"
                                        step="60"
                                        value={startTimeText}
                                        onChange={setDatetimeInputValue.bind(undefined, setStartTime)}
                                    />
                                </label>
                                <label>
                                    {t({ zh: "结束日期", en: "End Date" })}
                                    <input
                                        type="datetime-local"
                                        step="60"
                                        value={endTimeText}
                                        onChange={setDatetimeInputValue.bind(undefined, setEndTime)}
                                    />
                                </label>
                            </fieldset>
                            <div class="farpov_quick_select">
                                <small>{t({ zh: "快速选择范围：", en: "Quick Range:" })}</small>
                                <div class="farpov_button_group">
                                    <button
                                        type="button"
                                        class="outline secondary"
                                        disabled={processing}
                                        onClick={processing ? undefined : () => setStartTimeBasedOnEndTime(-1)}
                                        style={{ padding: "0.25rem 0.5rem", fontSize: "0.8rem" }}
                                    >
                                        {t({ zh: "1天", en: "1 Day" })}
                                    </button>
                                    {[3, 7, 30, 90, 180].map(days => (
                                        <button
                                            key={days}
                                            type="button"
                                            class="outline secondary"
                                            disabled={processing}
                                            onClick={processing ? undefined : () => setStartTimeBasedOnEndTime(-days)}
                                            style={{ padding: "0.25rem 0.5rem", fontSize: "0.8rem" }}
                                        >
                                            {t({ zh: `${days}天`, en: `${days} Days` })}
                                        </button>
                                    ))}
                                    <button
                                        type="button"
                                        class="outline secondary"
                                        disabled={processing}
                                        onClick={processing ? undefined : () => setStartTime(new Date("2020-01-01").getTime())}
                                        style={{ padding: "0.25rem 0.5rem", fontSize: "0.8rem" }}
                                    >
                                        {t({ zh: "所有", en: "All" })}
                                    </button>
                                </div>
                            </div>
                        </article>

                        {/* Step 2: Content Filtering */}
                        <article class="farpov_card">
                            <header>
                                <strong>2. {t({ zh: "内容过滤", en: "Filter Content" })}</strong>
                            </header>
                            <label style={{ display: "flex", alignItems: "center", marginBottom: filterEnabled ? "1rem" : "0" }}>
                                <input 
                                    type="checkbox" 
                                    checked={filterEnabled} 
                                    onChange={(e) => setFilterEnabled((e.target as HTMLInputElement).checked)} 
                                    style={{ margin: "0 0.5rem 0 0" }}
                                />
                                {t({ zh: "仅包含特定时段（如白天）", en: "Include specific time only (e.g. daytime)" })}
                            </label>
                            
                            {filterEnabled && (
                                <fieldset class="grid">
                                    <label>
                                        {t({ zh: "每日开始时间", en: "Daily Start Time" })}
                                        <input
                                            type="time"
                                            value={filterStartTime}
                                            onChange={(e) => setFilterStartTime((e.target as HTMLInputElement).value)}
                                        />
                                    </label>
                                    <label>
                                        {t({ zh: "每日结束时间", en: "Daily End Time" })}
                                        <input
                                            type="time"
                                            value={filterEndTime}
                                            onChange={(e) => setFilterEndTime((e.target as HTMLInputElement).value)}
                                        />
                                    </label>
                                </fieldset>
                            )}
                        </article>

                        {/* Step 3: Output Settings */}
                        <article class="farpov_card">
                            <header>
                                <strong>3. {t({ zh: "输出设置", en: "Output Settings" })}</strong>
                            </header>
                            <fieldset class="grid">
                                <label>
                                    {t({ zh: "视频质量", en: "Video Quality" })}: 
                                    <strong> {quality} </strong>
                                    <small>({t({ zh: "数值越小画质越好", en: "Lower value = Better quality" })})</small>
                                    <input
                                        type="range"
                                        min="15"
                                        max="50"
                                        step="1"
                                        value={quality}
                                        onInput={(evt) => {
                                            const val = Number.parseInt((evt.target as HTMLInputElement).value);
                                            if (val >= 15 && val <= 50) setQuality(val);
                                        }}
                                    />
                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", color: "var(--pico-muted-color)" }}>
                                        <span>{t({ zh: "高画质 (大文件)", en: "High Quality" })}</span>
                                        <span>{t({ zh: "低画质 (小文件)", en: "Low File Size" })}</span>
                                    </div>
                                </label>
                            </fieldset>
                        </article>

                        {/* Step 4: Audio Settings */}
                        <article class="farpov_card">
                            <header>
                                <strong>4. {t({ zh: "音频设置", en: "Audio Settings" })}</strong>
                            </header>
                            <label>
                                {t({ zh: "背景音乐 (可选)", en: "Background Music (Optional)" })}
                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.35rem", flexWrap: "wrap" }}>
                                    <input
                                        ref={audioInputRef}
                                        type="file"
                                        accept="audio/*"
                                        style={{ display: "none" }}
                                        onChange={(e) => {
                                            const file = (e.target as HTMLInputElement).files?.[0] || null;
                                            setAudioFile(file);
                                        }}
                                    />
                                    <button
                                        type="button"
                                        class="outline secondary"
                                        style={{ padding: "4px 10px", width: "auto", marginBottom: 0 }}
                                        onClick={() => audioInputRef.current?.click()}
                                    >
                                        {t({ zh: "选择文件", en: "Choose file" })}
                                    </button>
                                    <span style={{ color: "var(--pico-muted-color)", fontSize: "0.9rem" }}>
                                        {audioFile ? audioFile.name : t({ zh: "未选择任何文件", en: "No file chosen" })}
                                    </span>
                                    {audioFile && (
                                        <button
                                            type="button"
                                            class="outline secondary"
                                            style={{ padding: "4px 10px", width: "auto", marginBottom: 0 }}
                                            onClick={() => {
                                                setAudioFile(null);
                                                if (audioInputRef.current) audioInputRef.current.value = "";
                                            }}
                                        >
                                            {t({ zh: "清除", en: "Clear" })}
                                        </button>
                                    )}
                                </div>
                            </label>
                            {audioFile && (
                                <div style={{ marginTop: "0.5rem" }}>
                                    <audio controls src={URL.createObjectURL(audioFile)} style={{ width: "100%" }} />
                                </div>
                            )}
                        </article>

                        {/* Step 5: Generate & Result */}
                        <div class="farpov_actions">
                            <AdvanceUserRequiredBanner>
                                <button
                                    type="button"
                                    disabled={processing}
                                    onClick={processing ? undefined : onDownloadAndFFmpegClick}
                                    style={{ width: "100%", padding: "1rem", fontSize: "1.1rem", fontWeight: "bold" }}
                                >
                                    {processing ? t({ zh: "正在处理...", en: "Processing..." }) : t({ zh: "生成视频", en: "Generate Video" })}
                                </button>
                            </AdvanceUserRequiredBanner>
                            
                            {processing && (
                                <div style={{ marginTop: "1rem" }}>
                                    <progress value={prog[0].toString()} max={prog[1].toString()} />
                                    <small style={{ display: "block", textAlign: "center" }}>
                                        {Math.round((prog[0] / Math.max(prog[1], 1)) * 100)}%
                                    </small>
                                </div>
                            )}

                        </div>

                        {/* Result Preview */}
                        {videoUrl && (
                            <article class="farpov_card" style={{ marginTop: "2rem", borderColor: "var(--pico-primary-border)" }}>
                                <header>
                                    <strong>{t({ zh: "生成结果", en: "Result" })}</strong>
                                </header>
                                <div style={{ background: "#000", borderRadius: "var(--pico-border-radius)", overflow: "hidden" }}>
                                    <video 
                                        src={videoUrl} 
                                        controls 
                                        style={{ width: "100%", maxHeight: "60vh", display: "block" }} 
                                    />
                                </div>
                                <footer style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                                    {isMobile && (
                                        <button 
                                            class="outline" 
                                            onClick={() => {
                                                if (navigator.share) {
                                                    fetch(videoUrl).then(r => r.blob()).then(blob => {
                                                        const file = new File([blob], `video_${Date.now()}.mp4`, {type: "video/mp4"});
                                                        if (navigator.canShare && navigator.canShare({ files: [file] })) {
                                                            navigator.share({
                                                                files: [file],
                                                                title: 'My Video',
                                                                text: 'Created with Pi Camera'
                                                            }).catch(console.error);
                                                        } else {
                                                             window.open(videoUrl, "_blank");
                                                        }
                                                    });
                                                } else {
                                                    window.open(videoUrl, "_blank");
                                                }
                                            }}
                                            style={{ width: "auto", marginBottom: 0 }}
                                        >
                                            {t({zh: "分享/保存", en: "Share/Save"})}
                                        </button>
                                    )}
                                    <a 
                                        href={videoUrl} 
                                        download={`video_${Date.now()}.mp4`} 
                                        role="button" 
                                        class="primary"
                                        style={{ display: "inline-flex", alignItems: "center", marginBottom: 0 }}
                                    >
                                        <Icon name="download" style={{ marginRight: "0.5rem" }} />
                                        {t({ zh: "保存视频 (MP4)", en: "Save Video (MP4)" })}
                                    </a>
                                </footer>
                            </article>
                        )}
                    </div>
                </FFmpegLoader>
            </div>
            <FooterRecord />
        </>
    );
};
export default MakeVideoPage;
